import { Controller } from "@hotwired/stimulus"

const PARTICLE_COLORS = [
  "var(--color-primary)",
  "var(--color-accent)",
  "var(--color-secondary)",
  "var(--color-success)",
]

const PARTICLE_COUNT = 10

// Celebratory click feedback: an expanding ring plus a small confetti burst
// at the click point. Styles are inline (not Tailwind classes) so the nodes
// created here never depend on the utility scanner.
export default class extends Controller<HTMLElement> {
  burst(event: MouseEvent) {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    const rect = this.element.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top

    this.spawnRing(x, y)
    for (let i = 0; i < PARTICLE_COUNT; i++) this.spawnParticle(x, y, i)
  }

  private spawnRing(x: number, y: number) {
    const ring = document.createElement("span")
    Object.assign(ring.style, {
      position: "absolute",
      left: `${x - 22}px`,
      top: `${y - 22}px`,
      width: "44px",
      height: "44px",
      borderRadius: "9999px",
      border: "2px solid var(--color-primary)",
      pointerEvents: "none",
    })
    ring.setAttribute("aria-hidden", "true")
    this.element.append(ring)

    ring
      .animate(
        [
          { transform: "scale(0.2)", opacity: 0.9 },
          { transform: "scale(1.9)", opacity: 0 },
        ],
        { duration: 500, easing: "cubic-bezier(0.2, 0.7, 0.3, 1)" },
      )
      .finished.finally(() => ring.remove())
  }

  private spawnParticle(x: number, y: number, index: number) {
    const particle = document.createElement("span")
    const size = 4 + (index % 3) * 2
    const round = index % 3 === 0

    Object.assign(particle.style, {
      position: "absolute",
      left: `${x - size / 2}px`,
      top: `${y - size / 2}px`,
      width: `${size}px`,
      height: `${size}px`,
      borderRadius: round ? "9999px" : "2px",
      background: PARTICLE_COLORS[index % PARTICLE_COLORS.length],
      pointerEvents: "none",
    })
    particle.setAttribute("aria-hidden", "true")
    this.element.append(particle)

    const angle = (Math.PI * 2 * index) / PARTICLE_COUNT + (Math.random() - 0.5) * 0.6
    const distance = 28 + Math.random() * 22
    const dx = Math.cos(angle) * distance
    const dy = Math.sin(angle) * distance
    const rotation = (Math.random() - 0.5) * 180

    particle
      .animate(
        [
          { transform: "translate(0, 0) rotate(0deg) scale(1)", opacity: 1 },
          {
            transform: `translate(${dx}px, ${dy}px) rotate(${rotation}deg) scale(0.4)`,
            opacity: 0,
          },
        ],
        { duration: 550 + Math.random() * 200, easing: "cubic-bezier(0.15, 0.85, 0.3, 1)" },
      )
      .finished.finally(() => particle.remove())
  }
}
